import { cn } from "@universe/ui/lib/utils";
import { Mail, Phone } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

import { NoTranslate } from "@/components/no-translate";
import { useSupportContact } from "@/hooks/useSupportContact";

interface SupportContactProps {
  /**
   * Heading/label rendered above the contact rows. Each caller supplies its own
   * translated node so per-flow copy + styling stay intact.
   */
  heading?: ReactNode;
  /** Container classes (border, width) — merged with the base `space-y-3`. */
  className?: string;
  /** Accessible label for the email link (e.g. "Email support at …"). */
  emailAriaLabel?: string;
  /** Accessible label for the phone link. */
  phoneAriaLabel?: string;
}

/**
 * Whitelabel-aware support contact block (email + phone + hours), icon style.
 *
 * Resolves the brand's contact details via `useSupportContact()`. A channel the
 * brand hasn't provided (e.g. no phone number) renders the literal "Unavailable"
 * rather than being hidden, so the user always sees both channels. Renders
 * nothing only when the brand has NO contact channels at all (an opted-out
 * whitelabel). Shared by every icon-style error/empty state — recipient details,
 * the application-error modal, the account-state gate, and onboarding.
 *
 * (The kyc-verification screen keeps its own label-style block: a deliberately
 * different visual variant with `kycVerification` copy, so it is intentionally
 * NOT consolidated here.)
 *
 * Contact values are wrapped in `<NoTranslate>` so browser translation can't
 * mangle an email/phone (translation-tolerance C2/C4). Each row uses symmetric
 * ternary branches (both `<div>`s) so the fallback swap stays translation-safe.
 */
export function SupportContact({
  heading,
  className,
  emailAriaLabel,
  phoneAriaLabel,
}: SupportContactProps) {
  const t = useTranslations("support");
  const { supportEmail, supportPhone, supportPhoneHours } = useSupportContact();

  if (!supportEmail && !supportPhone) return null;

  return (
    <div className={cn("space-y-3", className)}>
      {heading}
      {supportEmail ? (
        <div className="flex items-center justify-center gap-2 text-sm">
          <Mail className="h-4 w-4 text-gray-400" />
          <a
            href={`mailto:${supportEmail}`}
            aria-label={emailAriaLabel}
            className="font-medium text-brand-strong hover:underline"
          >
            <NoTranslate>{supportEmail}</NoTranslate>
          </a>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 text-sm">
          <Mail className="h-4 w-4 text-gray-400" />
          <span className="font-medium text-muted-foreground">
            {t("notAvailable")}
          </span>
        </div>
      )}
      {supportPhone ? (
        <div className="flex items-center justify-center gap-2 text-sm">
          <Phone className="h-4 w-4 text-gray-400" />
          <a
            href={`tel:${supportPhone}`}
            aria-label={phoneAriaLabel}
            className="font-medium text-brand-strong hover:underline"
          >
            <NoTranslate>{supportPhone}</NoTranslate>
          </a>
          {supportPhoneHours && (
            <NoTranslate className="text-xs text-muted-foreground">
              ({supportPhoneHours})
            </NoTranslate>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 text-sm">
          <Phone className="h-4 w-4 text-gray-400" />
          <span className="font-medium text-muted-foreground">
            {t("notAvailable")}
          </span>
        </div>
      )}
    </div>
  );
}
